/**
 * Deep links from a feature screen into its Help Center guide.
 *
 * One helper, used everywhere, for two reasons. The slug and section id are
 * the contract between a screen and a guide, and a typo in either fails
 * silently — the Help Center simply opens at the top, or at the index, with
 * no error anywhere. Funnelling every call site through here at least makes
 * the contract greppable, and `GUIDE_SECTIONS` below names the sections that
 * screens actually point at so a guide rewrite has something to break against
 * rather than quietly orphaning a link.
 *
 * Links open in a new tab on purpose: reading the guide should never cost
 * someone the half-filled form they were looking at when they opened it.
 */

/** Slugs of guides the app links to directly. Must match `registry.ts`. */
export type GuideSlug =
  | 'dealer-feeds'
  | 'account-settings'
  | 'quickbooks'
  | 'netsuite'
  | 'credit-memos'
  | 'invoice-delivery'
  | 'partner-integrations'
  | 'past-due-work'
  | 'push-devices'
  | 'creating-work-orders'
  | 'customers'
  | 'roles-by-location-group'
  | 'service-splits'
  | 'signing-in'
  | 'override-pin'
  | 'new-invoicing';

/**
 * Section ids referenced from the UI, per guide.
 *
 * These MUST stay in step with the `sections[].id` values in the guide data
 * files. They are collected here rather than typed inline at each call site
 * so a guide restructure has one list to check.
 */
export const GUIDE_SECTIONS = {
  accountSettings: {
    rolesTab: 'roles-tab',
  },
  dealerFeeds: {
    whatItIs: 'what-it-is',
    thePieces: 'the-pieces',
    folderOrFile: 'folder-or-file',
    addStores: 'step-add-store',
    feedSetup: 'step-add-store',
    testIt: 'step-test',
    vendorDocument: 'step-vendor',
    howItRuns: 'how-it-runs',
    usingTheQueue: 'using-the-queue',
    monitoring: 'history',
    permissions: 'permissions',
    troubleshooting: 'troubleshooting',
    formats: 'formats',
  },
  quickbooks: {
    whatYouNeed: 'prerequisites',
    glossary: 'glossary',
    thePath: 'the-path',
    prepareQuickbooks: 'prepare-quickbooks',
    connect: 'setup',
    mapping: 'turn-connect-config',
    dryRun: 'dry-run',
    goLive: 'go-live',
    checklist: 'checklist',
    troubleshooting: 'troubleshooting',
  },
  partnerIntegrations: {
    inspectionsAndApprovals: 'inspections-and-approvals',
  },
  pastDueWork: {
    whatItMeans: 'overview',
    trail: 'trail',
  },
  serviceSplits: {
    setup: 'setup',
  },
  overridePin: {
    whatItIs: 'what-it-is',
    whenAsked: 'when-asked',
    accountPin: 'account-pin',
    locationPin: 'location-pin',
  },
  customers: {
    turnOn: 'turn-on',
    customersTab: 'customers-tab',
    customerPage: 'customer-page',
    jobInvoice: 'job-invoice',
    findByCustomer: 'find-by-customer',
    newJob: 'new-job',
    onWorkOrder: 'on-work-order',
    phoneApp: 'phone-app',
    required: 'required',
  },
  creatingWorkOrders: {
    chooseLocation: 'choose-location',
    seeLocation: 'see-location',
    changeTags: 'change-tags',
  },
  rolesByLocationGroup: {
    giveRole: 'give-role',
    following: 'following',
  },
  newInvoicing: {
    whatItIs: 'what-it-is',
    contacts: 'contacts',
    contactRoles: 'contact-roles',
    whoGetsWhat: 'who-gets-what',
    startFromRules: 'start-from-rules',
    storesDifferent: 'stores-different',
    options: 'options',
    checkUp: 'check-up',
    contactsChange: 'contacts-change',
    goBack: 'go-back',
    turnOn: 'turn-on',
    turnOff: 'turn-off',
  },
  invoiceDelivery: {
    coverage: 'coverage',
    checkUp: 'check-up',
    contacts: 'contacts',
    storeMap: 'store-map',
    whoGetsWhat: 'who-gets-what',
    sentEmails: 'sent-emails',
    accounting: 'accounting',
  },
  pushDevices: {
    overview: 'overview',
    yourDevices: 'your-devices',
    someoneElses: 'someone-elses-devices',
    troubleshooting: 'troubleshooting',
  },
} as const;

/**
 * Builds the in-app Help Center URL for a guide, optionally scrolled to one
 * of its sections.
 */
export const guideHref = (slug: GuideSlug | string, section?: string): string => `/${helpGuideSearch(slug, section)}`;

/** The query string that opens a guide (and optionally a section) in the in-app Help Center. */
export const helpGuideSearch = (slug?: string | null, section?: string | null): string => {
  if (!slug) return '?view=help';
  const base = `?view=help&guide=${encodeURIComponent(slug)}`;
  return section ? `${base}&section=${encodeURIComponent(section)}` : base;
};

/** Opens a guide in a new tab, so the user keeps whatever they were doing. */
export const openGuide = (slug: GuideSlug | string, section?: string): void => {
  window.open(guideHref(slug, section), '_blank', 'noopener');
};
